// Pure, framework-agnostic logic for the checkbox-based subtotal range editor. Operates only on
// ordered lists of ids so it can be shared between ProjectTable.tsx/DrawLineItemsTable.tsx
// (ProjectLineItemDTO[], position tracked via `serialNumber`) and PayappTable.tsx Step 2
// (RowData[], position tracked via plain array order) — each caller adapts its own data shape
// into an ordered id list before calling these, then reconciles the result back.
//
// Model: a subtotal's "pool" is every id strictly between the previous subtotal's anchor
// (exclusive) and the next subtotal's anchor (exclusive) — the only ids it could ever claim,
// since a range can never cross into an adjacent subtotal's anchor itself. The "checked" set is
// always a contiguous block of pool ids ending at the anchor (this invariant is established by
// getDefaultCheckedIds and preserved by every toggleRangeItem call).

export interface RangeToggleResult {
  poolIds: string[];
  anchorId: string;
  checkedIds: Set<string>;
}

// The pool of ids a subtotal at `anchorId` can possibly claim: everything strictly between the
// previous subtotal's anchor (exclusive) and the next subtotal's anchor (exclusive).
export function computeRangePool(orderedIds: string[], previousAnchorId: string | null, nextAnchorId: string | null): string[] {
  const startIdx = previousAnchorId ? orderedIds.indexOf(previousAnchorId) + 1 : 0;
  const rawEndIdx = nextAnchorId ? orderedIds.indexOf(nextAnchorId) : orderedIds.length;
  const endIdx = rawEndIdx < 0 ? orderedIds.length : rawEndIdx;
  return orderedIds.slice(Math.max(0, startIdx), endIdx);
}

// Default checked set: the last `range` pool ids ending at (and including) the anchor, or the
// entire pool up to the anchor if range is null/undefined (the existing default behavior).
export function getDefaultCheckedIds(poolIds: string[], anchorId: string, range: number | null | undefined): Set<string> {
  const anchorIdx = poolIds.indexOf(anchorId);
  if (anchorIdx < 0) return new Set(anchorId ? [anchorId] : []);
  const start = range != null && range > 0 ? Math.max(0, anchorIdx - range + 1) : 0;
  return new Set(poolIds.slice(start, anchorIdx + 1));
}

function findTopCheckedId(poolIds: string[], checkedIds: Set<string>): string | null {
  for (const id of poolIds) {
    if (checkedIds.has(id)) return id;
  }
  return null;
}

function findPreviousCheckedId(poolIds: string[], checkedIds: Set<string>, currentAnchorId: string): string | null {
  const anchorIdx = poolIds.indexOf(currentAnchorId);
  for (let i = anchorIdx - 1; i >= 0; i--) {
    if (checkedIds.has(poolIds[i])) return poolIds[i];
  }
  return null;
}

// Applies one checkbox toggle, per the rules:
//  - Checking a pool id below the anchor ("under the subtotal") pulls it up to sit immediately
//    after the anchor and makes it the new anchor.
//  - Checking a pool id above the checked block ("above the range") pulls it down to become the
//    new top of the checked block.
//  - Unchecking the anchor re-anchors to the previous checked id (no reorder needed — it already
//    sits in the right spot).
//  - Unchecking the topmost checked id just shrinks the block from the top (no reorder needed).
//  - Unchecking a middle id moves it out, to sit immediately after the anchor.
// Returns null for an invalid toggle (id not in the pool, or unchecking the only remaining item —
// a subtotal must always claim at least one line item).
export function toggleRangeItem(
  poolIds: string[],
  anchorId: string,
  checkedIds: Set<string>,
  toggledId: string,
  checked: boolean,
): RangeToggleResult | null {
  if (checked === checkedIds.has(toggledId)) {
    return { poolIds, anchorId, checkedIds };
  }

  const anchorIdx = poolIds.indexOf(anchorId);
  const toggledIdx = poolIds.indexOf(toggledId);
  if (anchorIdx < 0 || toggledIdx < 0) return null;

  if (checked) {
    const newPool = poolIds.slice();
    newPool.splice(toggledIdx, 1);

    if (toggledIdx > anchorIdx) {
      // Below the anchor — becomes the new anchor, inserted right after the current one.
      const newAnchorIdx = newPool.indexOf(anchorId);
      newPool.splice(newAnchorIdx + 1, 0, toggledId);
      return { poolIds: newPool, anchorId: toggledId, checkedIds: new Set(Array.from(checkedIds).concat(toggledId)) };
    }

    // Above the checked block — becomes the new top of it.
    const topCheckedId = findTopCheckedId(poolIds, checkedIds);
    const topIdx = topCheckedId ? newPool.indexOf(topCheckedId) : newPool.indexOf(anchorId);
    newPool.splice(topIdx, 0, toggledId);
    return { poolIds: newPool, anchorId, checkedIds: new Set(Array.from(checkedIds).concat(toggledId)) };
  }

  // Unchecking — must keep at least one item claimed.
  if (checkedIds.size <= 1) return null;

  if (toggledId === anchorId) {
    const newAnchorId = findPreviousCheckedId(poolIds, checkedIds, anchorId);
    if (!newAnchorId) return null;
    const nextChecked = new Set(checkedIds);
    nextChecked.delete(toggledId);
    return { poolIds, anchorId: newAnchorId, checkedIds: nextChecked };
  }

  const topCheckedId = findTopCheckedId(poolIds, checkedIds);
  if (toggledId === topCheckedId) {
    const nextChecked = new Set(checkedIds);
    nextChecked.delete(toggledId);
    return { poolIds, anchorId, checkedIds: nextChecked };
  }

  // Middle item — moves out, to sit immediately after the anchor.
  const newPool = poolIds.slice();
  newPool.splice(toggledIdx, 1);
  const newAnchorIdx = newPool.indexOf(anchorId);
  newPool.splice(newAnchorIdx + 1, 0, toggledId);
  const nextChecked = new Set(checkedIds);
  nextChecked.delete(toggledId);
  return { poolIds: newPool, anchorId, checkedIds: nextChecked };
}
